import { CSSProperties, FC, useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

import { metric } from 'features/metric';
import { useCart } from 'shared/lib/hooks/useCart';
import { Counter } from 'widgets/Counter';
import { useFavorite } from 'shared/lib/hooks/useFavorite';
import useDebounce from 'shared/lib/hooks/useDebounce';
import { toNumberWithSpaces } from 'shared/lib/formatters';
import type { IGoodInCart } from 'entities/products';
import { getImageVariantProps } from 'shared/lib/images';
import PlaceholderImage from 'assets/images/placeholder_small.png';
import { Icon } from '@nebo-team-dev/vobaza.ui.icon/dist';

import styles from './styles.module.scss';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';

type Props = {
  good: IGoodInCart;
  deleteItem: (id: number, quantity: number) => void;
  changeItem: (id: number, quantity: number) => Promise<number>;
  setIsCheckoutDisabled?: (boolean) => void;
  className?: string;
  isCartLoading?: boolean;
  restoreItem: (id: number) => void;
};

const CartListItem: FC<Props> = ({
  good,
  deleteItem,
  changeItem,
  setIsCheckoutDisabled,
  className,
  isCartLoading = false,
  restoreItem,
}) => {
  const [count, setCount] = useState(good.quantity);
  const { addToCart, isLoading: cartHookLoading } = useCart(good.product);
  const { currentFavorite, toggleFavorite, isLoading, setIsLoading } = useFavorite(good);
  const basketItems = useSelector((store: RootState) => store.goods.basketItems);
  const deleteItemHandler = () => {
    deleteItem(good.product.id, good.quantity);
  };

  const onRestore = () => {
    restoreItem(good.product.id);
    addToCart({ quantity: good.quantity, withModal: false, withCartChange: false });
  };
  const removeStyles: CSSProperties = good.isRemoved ? { opacity: 0.5, pointerEvents: 'none' } : {};
  const changeItemCount = async () => {
    setIsLoading(true);
    try {
      const quantity = await changeItem(good.product.id, count - good.quantity);
      if (quantity) {
        setCount(good.quantity + quantity);
      } else {
        setCount(good.quantity);
      }
      setIsCheckoutDisabled(false);
    } catch (error) {
      setCount(good.quantity);
    } finally {
      setIsLoading(false);
    }
  };
  const onFavoriteClick = async () => {
    try {
      await toggleFavorite();
      metric.reachGoal('wishlist');
    } catch (e) {
      console.error(e);
    }
  };
  const increaseCount = () => {
    setCount(count + 1);
  };

  const decreaseCount = () => {
    count > 1 && setCount(count - 1);
  };
  const debouncedChangeItemCount = useDebounce(changeItemCount, 500);

  useEffect(() => {
    if (!isLoading) {
      setIsCheckoutDisabled(true);
      debouncedChangeItemCount();
    } else {
      setIsLoading(false);
    }
  }, [count]);

  useEffect(() => {
    setIsLoading(isCartLoading || cartHookLoading);
  }, [isCartLoading, cartHookLoading]);
  useEffect(() => {
    const goodInCartQuantity = basketItems.find((item) => item.id === good.product.id)?.quantity;
    setCount(goodInCartQuantity || 0);
  }, [basketItems]);
  return (
    <div className={`${styles.cartListItem} ${className}`}>
      <div style={removeStyles} className={styles.cartListItemImageBlock}>
        <Link href={`/product/${good.product.slug}`} passHref target="_blank" className={styles.cartListItemLink}>
          {good.product.main_image ? (
            <Image {...getImageVariantProps(good.product.main_image.variants, 'small_webp')} alt={good.product.name} />
          ) : (
            <Image src={PlaceholderImage} alt={good.product.name} />
          )}
        </Link>
      </div>

      <div className={styles.infoBlock}>
        <div className={styles.infoBlock__info}>
          <div style={removeStyles}>
            <Link href={`/product/${good.product.slug}`} passHref className={styles.cartListItemTitle} target="_blank">
              {good.product?.seo?.page_name || good.product.name}
            </Link>
          </div>
          <div style={removeStyles} className={styles.cartListItemPrice}>
            <p className={styles.infoBlock__price}>{toNumberWithSpaces(good.price)} ₽</p>
            {good.product.list_price && (
              <p className={styles.cartListItemPriceOld}>{toNumberWithSpaces(good.list_price)} ₽</p>
            )}
          </div>
        </div>
        {Boolean(good.isRemoved) ? (
          <button className={styles.restore} onClick={onRestore}>
            восстановить товар
          </button>
        ) : (
          <div className={styles.infoBlock__buttonsBlock}>
            <div className={styles.infoBlock__icons}>
              <button
                type="button"
                disabled={isLoading}
                className={isLoading ? styles.pending : null}
                onClick={deleteItemHandler}
              >
                <Icon name="Trash" />
              </button>
              <button
                type="button"
                disabled={isLoading}
                className={isLoading ? styles.pending : null}
                onClick={onFavoriteClick}
              >
                <Icon className={currentFavorite ? styles.fillIcon : ''} name="Favorite" />
              </button>
            </div>
            <div className={styles.infoBlock__counter}>
              <p className={styles.cartListItemPriceForOne}>{toNumberWithSpaces(good.product.price)} ₽ / шт</p>
              <Counter
                variant="secondary"
                isOneDisable
                isLoading={isLoading}
                value={count}
                decreaseHandler={decreaseCount}
                increaseHandler={increaseCount}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default CartListItem;
